import React, { useEffect, useMemo, useState } from "react";
import {
  FaBalanceScale,
  FaClock,
  FaCookieBite,
  FaDatabase,
  FaEnvelope,
  FaExternalLinkAlt,
  FaFileAlt,
  FaGlobe,
  FaInfoCircle,
  FaLock,
  FaShieldAlt,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScrollToTop";
import { getSettings } from "../../services/settingsServices";
import Footer from "../Home/HomePage/Footer";
import ScrollIndicator from "../../components/ScrollIndicator/ScrollIndicator";

const DEFAULT_SETTINGS = {
  siteweb_club: "",
  nom_club: "",
  adresse_club: "",
  responsable_club: "",
  mail_club: "",
  siret_club: "",
  tel_club: "",
};

const getDisplayValue = (value, fallback = "Non renseigné") => {
  const clean = String(value || "").trim();
  return clean || fallback;
};

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const normalizeWebsite = (site = "") => {
  const clean = String(site || "").trim();

  if (!clean && typeof window !== "undefined") {
    return window.location.origin;
  }

  return clean || "";
};

const SectionCard = ({ id, icon: Icon, title, children }) => (
  <article
    id={id}
    className="scroll-mt-32 rounded-[2rem] border border-slate-200 bg-white/90 p-6 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/85 sm:p-8"
  >
    <div className="mb-5 flex items-start gap-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon />
      </div>

      <div>
        <h2 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white sm:text-3xl">
          {title}
        </h2>
      </div>
    </div>

    <div className="space-y-4 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base">
      {children}
    </div>
  </article>
);

const SummaryCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-[1.7rem] border border-slate-200 bg-white/85 p-5 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
    <Icon className="mb-4 text-2xl text-primary" />
    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
      {label}
    </p>
    <p className="mt-2 break-words font-barlow text-xl font-bold text-slate-950 dark:text-white">
      {value}
    </p>
  </div>
);

const InfoList = ({ items = [] }) => (
  <ul className="grid gap-3 md:grid-cols-2">
    {items.map((item) => (
      <li
        key={item}
        className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold leading-6 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
      >
        {item}
      </li>
    ))}
  </ul>
);

export default function PolitiqueConfidentialitePage() {
  useScrollToTop();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted || !clubSettings) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...clubSettings,
          siteweb_club:
            clubSettings.siteweb_club ||
            clubSettings.website ||
            clubSettings.site_url ||
            "",
          mail_club:
            clubSettings.mail_club ||
            clubSettings.email_club ||
            clubSettings.email ||
            "",
          tel_club:
            clubSettings.tel_club ||
            clubSettings.telephone_club ||
            clubSettings.phone ||
            "",
          adresse_club:
            clubSettings.adresse_club ||
            clubSettings.address ||
            [clubSettings.adresse, clubSettings.code_postal, clubSettings.ville]
              .filter(Boolean)
              .join(" "),
          responsable_club:
            clubSettings.responsable_club ||
            clubSettings.responsable_publication ||
            clubSettings.president_club ||
            "",
        });
      } catch (error) {
        console.error(
          "Erreur lors du chargement de la politique de confidentialité :",
          error
        );
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const siteUrl = useMemo(
    () => normalizeWebsite(settings.siteweb_club),
    [settings.siteweb_club]
  );

  const mailHref = useMemo(() => {
    return settings.mail_club ? `mailto:${settings.mail_club}` : "";
  }, [settings.mail_club]);

  const currentDate = new Date().toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });

  const navItems = [
    ["Introduction", "introduction"],
    ["Responsable du traitement", "responsable"],
    ["Données collectées", "donnees-collectees"],
    ["Finalités", "finalites"],
    ["Base légale", "base-legale"],
    ["Destinataires", "destinataires"],
    ["Conservation", "conservation"],
    ["Sécurité", "securite"],
    ["Cookies", "cookies"],
    ["Droits", "droits"],
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <main>
        <section className="relative overflow-hidden bg-slate-950 px-4 pb-16 pt-36 text-white sm:px-6 lg:px-8 lg:pb-20 lg:pt-40">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:44px_44px] opacity-20" />

          <div className="relative mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/70 backdrop-blur-xl">
                <FaShieldAlt className="text-primary" />
                Données personnelles
              </div>

              <h1 className="mt-6 font-barlow text-5xl font-bold leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
                Politique de
                <br />
                confidentialité
              </h1>

              <p className="mt-6 max-w-2xl text-base leading-8 text-white/75 sm:text-lg">
                Cette page explique comment les données personnelles peuvent
                être collectées et utilisées dans le cadre du site web du club,
                des formulaires, des demandes de contact et des inscriptions.
              </p>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-5 md:grid-cols-3">
              <SummaryCard
                icon={FaUsers}
                label="Structure"
                value={getDisplayValue(settings.nom_club, "Club / association")}
              />

              <SummaryCard
                icon={FaGlobe}
                label="Site internet"
                value={getDisplayValue(siteUrl)}
              />

              <SummaryCard
                icon={FaEnvelope}
                label="Contact données"
                value={getDisplayValue(settings.mail_club)}
              />
            </div>

            <div className="mt-10 grid gap-8 lg:grid-cols-[280px_1fr]">
              <aside className="hidden lg:block">
                <div className="sticky top-32 rounded-[2rem] border border-slate-200 bg-white/90 p-5 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/85">
                  <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Sommaire
                  </p>

                  <nav className="flex flex-col gap-2">
                    {navItems.map(([label, id]) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className="rounded-2xl px-4 py-3 text-sm font-bold text-slate-600 transition hover:bg-orange-50 hover:text-primary dark:text-slate-300 dark:hover:bg-white/10"
                      >
                        {label}
                      </a>
                    ))}
                  </nav>

                  <div className="mt-5 rounded-2xl bg-orange-50 p-4 text-sm leading-6 text-orange-800 dark:bg-orange-500/10 dark:text-orange-300">
                    <div className="mb-2 flex items-center gap-2 font-bold">
                      <FaInfoCircle />
                      Mise à jour
                    </div>
                    Dernière mise à jour automatique : {currentDate}.
                  </div>
                </div>
              </aside>

              <div className="space-y-8">
                <SectionCard
                  id="introduction"
                  icon={FaFileAlt}
                  title="1. Introduction"
                >
                  <p>
                    La présente politique de confidentialité a pour objectif
                    d’informer les utilisateurs du site sur la manière dont leurs
                    données personnelles peuvent être collectées, utilisées,
                    conservées et protégées.
                  </p>

                  <p>
                    Elle concerne les données collectées dans le cadre de la
                    consultation du site, de l’utilisation des formulaires de
                    contact ou de demande d’inscription, ainsi que des échanges
                    réalisés avec le club ou l’association.
                  </p>
                </SectionCard>

                <SectionCard
                  id="responsable"
                  icon={FaUserCheck}
                  title="2. Responsable du traitement"
                >
                  <p>
                    Le responsable du traitement des données personnelles
                    collectées via le site est :
                  </p>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                        Club / association
                      </p>
                      <p className="mt-1 font-bold text-slate-800 dark:text-slate-100">
                        {getDisplayValue(settings.nom_club, "Club / association")}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                        Contact
                      </p>
                      <p className="mt-1 break-words font-bold text-slate-800 dark:text-slate-100">
                        {settings.mail_club ? (
                          <a
                            href={mailHref}
                            className="text-primary hover:underline"
                          >
                            {settings.mail_club}
                          </a>
                        ) : (
                          "Email non renseigné"
                        )}
                      </p>
                    </div>
                  </div>

                  <p>
                    Le prestataire technique de l’application web peut intervenir
                    uniquement comme support technique ou sous-traitant technique
                    pour assurer le bon fonctionnement, la maintenance et
                    l’hébergement applicatif du site, selon les modalités
                    définies avec le club.
                  </p>
                </SectionCard>

                <SectionCard
                  id="donnees-collectees"
                  icon={FaDatabase}
                  title="3. Données susceptibles d’être collectées"
                >
                  <p>
                    Selon les fonctionnalités activées sur le site, les données
                    suivantes peuvent être collectées :
                  </p>

                  <InfoList
                    items={[
                      "Données d’identification : nom, prénom, identité du demandeur ou de l’adhérent.",
                      "Coordonnées : email, téléphone, adresse postale, commune.",
                      "Données liées à une inscription : date de naissance, groupe souhaité, licence, poids, autorisation photo, remarques utiles.",
                      "Données transmises via les formulaires : message, objet de la demande, informations saisies volontairement.",
                      "Données techniques : adresse IP, type de navigateur, date et heure de connexion, logs techniques nécessaires à la sécurité et au fonctionnement du site.",
                    ]}
                  />

                  <p>
                    Les utilisateurs sont invités à ne transmettre que les
                    informations nécessaires à leur demande et à éviter de
                    communiquer des données sensibles non demandées.
                  </p>
                </SectionCard>

                <SectionCard
                  id="finalites"
                  icon={FaInfoCircle}
                  title="4. Finalités de traitement"
                >
                  <p>
                    Les données personnelles collectées peuvent être utilisées
                    pour les finalités suivantes :
                  </p>

                  <InfoList
                    items={[
                      "Répondre aux demandes envoyées via le formulaire de contact.",
                      "Traiter et suivre les demandes d’inscription au club.",
                      "Permettre la gestion administrative de la relation avec les adhérents ou futurs adhérents.",
                      "Transmettre les informations nécessaires aux responsables du club.",
                      "Assurer la sécurité, la maintenance et le bon fonctionnement technique du site.",
                      "Produire des statistiques internes de consultation lorsque cette fonctionnalité est activée.",
                    ]}
                  />
                </SectionCard>

                <SectionCard
                  id="base-legale"
                  icon={FaBalanceScale}
                  title="5. Bases légales des traitements"
                >
                  <p>
                    Les traitements de données personnelles peuvent reposer,
                    selon les cas, sur :
                  </p>

                  <InfoList
                    items={[
                      "Le consentement de l’utilisateur lorsqu’il remplit volontairement un formulaire.",
                      "L’intérêt légitime du club à répondre aux demandes, sécuriser le site et assurer sa communication associative.",
                      "L’exécution de mesures précontractuelles ou contractuelles lorsque la demande concerne une inscription ou une adhésion.",
                      "Le respect d’obligations légales ou administratives lorsque certaines données doivent être conservées dans le cadre de la vie associative.",
                    ]}
                  />
                </SectionCard>

                <SectionCard
                  id="destinataires"
                  icon={FaUsers}
                  title="6. Destinataires des données"
                >
                  <p>
                    Les données collectées sont destinées uniquement aux
                    personnes habilitées à les traiter dans le cadre de leurs
                    missions.
                  </p>

                  <InfoList
                    items={[
                      "Les responsables ou administrateurs du club autorisés à gérer les messages et inscriptions.",
                      "Les personnes chargées du suivi administratif ou sportif lorsque cela est nécessaire.",
                      "Le prestataire technique de l’application web, uniquement pour les besoins de fonctionnement, maintenance, sécurité ou support.",
                      "L’hébergeur technique du site, dans le cadre de l’hébergement des données.",
                      "Les autorités compétentes lorsque la loi l’exige.",
                    ]}
                  />

                  <p>
                    Les données ne sont pas revendues à des tiers. Elles ne sont
                    pas utilisées à des fins commerciales indépendantes du club,
                    sauf information spécifique et consentement lorsque cela est
                    requis.
                  </p>
                </SectionCard>

                <SectionCard
                  id="conservation"
                  icon={FaClock}
                  title="7. Durée de conservation"
                >
                  <p>
                    Les données personnelles sont conservées pendant une durée
                    limitée, proportionnée aux finalités pour lesquelles elles
                    ont été collectées.
                  </p>

                  <InfoList
                    items={[
                      "Messages de contact : durée nécessaire au traitement de la demande et au suivi éventuel.",
                      "Demandes d’inscription : durée nécessaire à l’étude de la demande, puis à la gestion administrative si l’inscription est confirmée.",
                      "Données liées à la vie associative : durée nécessaire à la gestion du club et aux obligations administratives applicables.",
                      "Logs techniques : durée limitée nécessaire à la sécurité, au diagnostic et au bon fonctionnement du site.",
                    ]}
                  />

                  <p>
                    Lorsque les données ne sont plus nécessaires, elles peuvent
                    être supprimées, archivées ou anonymisées selon les besoins
                    administratifs, techniques ou légaux du club.
                  </p>
                </SectionCard>

                <SectionCard
                  id="securite"
                  icon={FaLock}
                  title="8. Sécurité des données"
                >
                  <p>
                    Le club et le prestataire technique mettent en œuvre des
                    mesures raisonnables afin de protéger les données
                    personnelles contre l’accès non autorisé, la modification, la
                    perte, la divulgation ou la destruction.
                  </p>

                  <p>
                    Ces mesures peuvent notamment inclure le contrôle des accès à
                    l’espace d’administration, l’authentification des
                    utilisateurs autorisés, la sécurisation de l’hébergement et
                    la maintenance technique de l’application.
                  </p>

                  <p>
                    Aucun système n’étant totalement exempt de risque,
                    l’utilisateur est invité à rester vigilant et à ne pas
                    transmettre d’informations inutiles ou sensibles via les
                    formulaires.
                  </p>
                </SectionCard>

                <SectionCard
                  id="cookies"
                  icon={FaCookieBite}
                  title="9. Cookies et traceurs"
                >
                  <p>
                    Le site peut utiliser des cookies ou traceurs nécessaires à
                    son bon fonctionnement, à la sécurité, à la conservation de
                    certaines préférences ou à la mesure de fréquentation lorsque
                    cette fonctionnalité est activée.
                  </p>

                  <p>
                    Certains cookies strictement nécessaires au fonctionnement du
                    service peuvent être utilisés sans consentement préalable.
                    Les cookies soumis au consentement doivent faire l’objet
                    d’une information et d’un choix adapté lorsque ces outils
                    sont activés.
                  </p>

                  <p>
                    L’utilisateur peut paramétrer son navigateur afin de limiter,
                    bloquer ou supprimer les cookies. Le refus de certains
                    cookies peut toutefois dégrader l’accès à certaines
                    fonctionnalités du site.
                  </p>
                </SectionCard>

                <SectionCard
                  id="droits"
                  icon={FaShieldAlt}
                  title="10. Droits des utilisateurs"
                >
                  <p>
                    Conformément à la réglementation applicable en matière de
                    protection des données, l’utilisateur dispose notamment d’un
                    droit d’accès, de rectification, d’effacement, de limitation,
                    d’opposition et, lorsque cela est applicable, d’un droit à la
                    portabilité de ses données.
                  </p>

                  <p>
                    Pour exercer ses droits, l’utilisateur peut contacter le club
                    à l’adresse suivante :{" "}
                    {settings.mail_club ? (
                      <a
                        href={mailHref}
                        className="font-bold text-primary hover:underline"
                      >
                        {settings.mail_club}
                      </a>
                    ) : (
                      <strong>email de contact non renseigné</strong>
                    )}
                    .
                  </p>

                  <p>
                    Afin de protéger les données personnelles, une preuve
                    d’identité peut être demandée lorsque cela est nécessaire
                    pour confirmer l’identité de la personne à l’origine de la
                    demande.
                  </p>

                  <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5 text-orange-800 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                    <div className="mb-2 flex items-center gap-2 font-bold">
                      <FaExternalLinkAlt />
                      Réclamation
                    </div>
                    <p>
                      En cas de difficulté non résolue, l’utilisateur peut
                      s’informer ou introduire une réclamation auprès de la CNIL
                      via son site officiel.
                    </p>
                    <a
                      href="https://www.cnil.fr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex font-bold underline"
                    >
                      Accéder au site de la CNIL
                    </a>
                  </div>
                </SectionCard>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollIndicator />
      </main>
    </div>
  );
}